/**
 * PersonalPresetModule — renders a STYLED personal world (2026-08-30,
 * photoreal pass): the world_preset row picks a designed home world and this
 * module builds it deterministically with the full PBR treatment —
 *
 *   TEXTURES   Higgsfield-generated tileables + derived normal/roughness
 *              (textures/presets/*, the hive-ground pipeline): meadow grass,
 *              coastal cliff rock, lime plaster, thatch, concrete pavers,
 *              rippled sea. Vertex colors stay on as TINT so cliff faces and
 *              seabed shade the grass map.
 *   LIGHTING   a per-preset SUN (shadow-casting DirectionalLight): golden
 *              hour over the sea for the mansion, soft noon for the cottage,
 *              dusk for the penthouse — plus tuned hemisphere fill and scene
 *              FOG (saved and restored on leave).
 *   BUILDINGS  every preset now has TWO enterable interiors (the standard
 *              E-at-the-door machinery + themed home furnishing): the
 *              mansion and its glass POOL ANNEX; the penthouse pavilion and
 *              the SKY LOUNGE; the cottage and its GARDEN WORKSHOP.
 *   DETAIL     mullioned glazing, roof overhangs, a balustraded mansion
 *              balcony, cottage shutters + climbing rose + lantern, string
 *              lights sagging across the sky terrace, an animated sea.
 *
 * Grounds stay ANALYTIC (lib/personal_presets — clamp/terrain/placement
 * agree by construction); safety slabs sit 0.1 m under every pad (the
 * ground-of-last-resort law).
 */
import * as THREE from 'three/webgpu';
import { fireAnchor } from '../lib/vfx/fire_system.js';
import type { App, IModule } from 'enginex';
import { disposeLater } from '../lib/gpu_dispose.js';
import { seatHemisphereUp } from '../lib/local_origin.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import {
  presetGroundY, PRESET_HOME, WORLD_PRESETS, type WorldPresetKey,
  MANSION_CLIFF_X1, MANSION_BEACH_X1, MANSION_SEA_Y, MANSION_RAMP_Z0, MANSION_RAMP_Z1, PENT_DECK_HW, PENT_DECK_HD, PENT_DECK_Y,
} from '../lib/personal_presets.js';
import { enterables, type EnterableBuilding } from '../lib/building_interiors.js';
import type { PhysicsModule } from './PhysicsModule.js';
import type { AssetModule } from './AssetModule.js';
import { prefabAssetId } from '../lib/player_models.js';
import { markShared } from '../lib/defer_dispose.js';
import { collectMeshParts } from '../lib/instancing.js';
import { setUnderwaterFog } from './AtmosphereModule.js';
import { sfx } from '../lib/sfx.js';
import { buildPlasterMaterial } from '../lib/plaster_material.js';

const TERRAIN_SIZE = 1024;
const TERRAIN_SEG = 160;

interface HomeSpec {
  id: string; name: string;
  x: number; z: number; yaw: number; w: number; d: number; h: number; groundY: number;
  body: number; trim: number;
  /** 'plaster' binds the plaster PBR set to the shell. */
  wall?: 'plaster' | 'none';
}

export class PersonalPresetModule implements IModule {
  readonly name = 'PersonalPreset';
  private scene!: THREE.Scene;
  private root: THREE.Group | null = null;
  private builtKey = '';
  private colliderIds: string[] = [];
  private enterableIds: string[] = [];
  private subbed = false;
  private savedFog: THREE.Fog | THREE.FogExp2 | null = null;
  private hadFog = false;
  private seaMat: THREE.MeshStandardMaterial | null = null;
  private hemi: THREE.HemisphereLight | null = null;
  private activePreset: WorldPresetKey | null = null;
  private swimming = false;
  private lastStrokeAt = 0;
  private onSwimKey: ((e: KeyboardEvent) => void) | null = null;
  private refreshTimer: number | null = null;
  private swimTimer: number | null = null;

  constructor(private readonly physics: PhysicsModule, private readonly assets: AssetModule) {}

  init(app: App): void {
    this.scene = app.world.scene;
    bus.on('atlas:world-entered', () => this.refresh());
    bus.on('atlas:account-ready', () => { this.ensureSub(); this.refresh(); });
    this.refreshTimer = window.setInterval(() => this.refresh(), 2500);
    this.swimTimer = window.setInterval(() => {
      // The sea breathes: scroll the ripple normal (wall-clock — cheap, shared).
      if (this.seaMat?.normalMap) {
        const t = Date.now() / 1000;
        this.seaMat.normalMap.offset.set((t * 0.008) % 1, (t * 0.0045) % 1);
        if (this.seaMat.map) this.seaMat.map.offset.set((t * 0.004) % 1, (t * 0.0022) % 1);
      }
      if (this.hemi) seatHemisphereUp(this.hemi); // THE HEMISPHERE'S UP: the rebase re-seats the scene on entry (and every 10 km) — left at (0, 1, 0) the sky colour came from the horizon
      this.swimTick();
    }, 66);
    // A swim STROKE: space while in the water kicks upward (launch works
    // regardless of grounding — the jump-pad path).
    this.onSwimKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || !this.swimming) return;
      const now = Date.now();
      if (now - this.lastStrokeAt < 350) return;
      this.lastStrokeAt = now;
      this.physics.launch(2.4);
    };
    window.addEventListener('keydown', this.onSwimKey);
  }

  /** REAL water (mansion): below the surface the world goes underwater —
   *  low gravity (slow sink), space strokes to rise, fog + splash on the
   *  transitions. Re-asserts atlas.submerged each tick (the two-owner
   *  convergence law shared with the ocean module and water devices). */
  private swimTick(): void {
    const inMansion = this.activePreset === 'mansion' && this.root !== null;
    const feetY = atlas.localY;
    const eyeY = feetY + 1.5;
    const groundHere = inMansion ? presetGroundY('mansion', atlas.localX, atlas.localZ) : 99;
    const inWaterColumn = inMansion && groundHere < MANSION_SEA_Y - 0.15 && feetY < MANSION_SEA_Y - 0.25;
    const headUnder = inWaterColumn && eyeY < MANSION_SEA_Y - 0.05;
    if (inWaterColumn && !this.swimming) {
      this.swimming = true;
      this.physics.gravityScale = 0.12;
      sfx('sfx_water_splash', { gain: 0.5, cooldownMs: 600 });
    } else if (!inWaterColumn && this.swimming) {
      this.swimming = false;
      this.physics.gravityScale = 1;
      setUnderwaterFog(null);
      atlas.submerged = false;
    }
    if (this.swimming) {
      if (headUnder) { setUnderwaterFog({ color: 0x0a2a38, density: 0.04 }); atlas.submerged = true; }
      else { setUnderwaterFog(null); atlas.submerged = false; }
    }
  }

  private ensureSub(): void {
    if (this.subbed || !atlas.conn) return;
    this.subbed = true;
    try { atlas.conn.subscriptionBuilder().subscribe(['SELECT * FROM world_preset']); } catch { this.subbed = false; }
  }

  private presetOf(worldId: bigint): WorldPresetKey | null {
    try {
      const pr = (atlas.conn?.db as unknown as { worldPreset?: { worldId: { find(id: bigint): { preset: string } | null | undefined } } })?.worldPreset?.worldId.find(worldId);
      if (pr && WORLD_PRESETS.some((p) => p.key === pr.preset)) return pr.preset as WorldPresetKey;
    } catch { /* pre-publish */ }
    return null;
  }

  private refresh(): void {
    this.ensureSub();
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) { this.release(); return; }
    let kind = '';
    try { for (const w of conn.db.world.iter()) if (w.worldId === wid) { kind = w.kind; break; } } catch { /* churn */ }
    const preset = kind === 'personal' ? this.presetOf(wid) : null;
    if (!preset || preset === 'blank') { this.release(); return; }
    const key = `${wid}|${preset}`;
    if (this.root && this.builtKey === key) return;
    this.release();
    this.build(preset);
    this.builtKey = key;
    this.activePreset = preset;
  }

  // ── Texture binding (graceful when the store is down) ──────────────────
  private bindSet(mat: THREE.MeshStandardMaterial, name: string, repeat: number, normalScale = 1): void {
    const bind = (tex: THREE.Texture): THREE.Texture => {
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(repeat, repeat);
      tex.anisotropy = 8;
      return tex;
    };
    void Promise.all([
      this.assets.loadTexture(`textures/presets/${name}.png`),
      this.assets.loadTexture(`textures/presets/${name}_n.png`, false),
      this.assets.loadTexture(`textures/presets/${name}_r.png`, false),
    ]).then(([albedo, normal, rough]) => {
      mat.map = bind(albedo);
      mat.normalMap = bind(normal);
      mat.normalScale.set(normalScale, normalScale);
      mat.roughnessMap = bind(rough);
      mat.needsUpdate = true;
    }).catch(() => { /* store down / not yet generated — tinted geometry still reads */ });
  }

  // ── Build ──────────────────────────────────────────────────────────────
  private build(preset: WorldPresetKey): void {
    const root = new THREE.Group();
    root.name = 'personal-preset';
    this.root = root;
    this.scene.add(root);
    this.hadFog = this.scene.fog !== null;
    this.savedFog = this.scene.fog as THREE.Fog | null;
    this.buildGround(root, preset);
    this.buildLighting(root, preset);
    if (preset === 'mansion') this.buildMansion(root);
    else if (preset === 'penthouse') this.buildPenthouse(root);
    else if (preset === 'cottage') this.buildCottage(root);
  }

  private addCol(id: string, half: [number, number, number], x: number, y: number, z: number, yaw: number): void {
    this.physics.addStaticCollider(id, { shape: 'box', halfExtents: [half[0] / 2, half[1] / 2, half[2] / 2] }, { x, y, z, yaw, scale: 1 });
    this.colliderIds.push(id);
  }

  private buildGround(root: THREE.Group, preset: WorldPresetKey): void {
    const geo = new THREE.PlaneGeometry(TERRAIN_SIZE, TERRAIN_SIZE, TERRAIN_SEG, TERRAIN_SEG);
    geo.rotateX(-Math.PI / 2);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const colors = new Float32Array(pos.count * 3);
    const cGrass = new THREE.Color(0xffffff);           // let the map carry the color
    const cRock = new THREE.Color(0x9a8f83);            // tint toward rock on the cliff
    const cBed = new THREE.Color(0x51604f);
    const cStreet = new THREE.Color(0x565b64);
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), z = pos.getZ(i);
      const h = presetGroundY(preset, x, z);
      pos.setY(i, h);
      let c = cGrass;
      if (preset === 'mansion') {
        if (x > 38 && h > -5.5) c = cRock;
        else if (h <= -5.5) c = cBed;
      } else if (preset === 'penthouse' && h < 1) c = cStreet;
      colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geo.computeVertexNormals();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.92, metalness: 0.02, color: 0x9aa886 });
    // Grass carries the meadow + plateau; the penthouse deck slab has its own
    // paver material (the street below barely reads and stays tinted).
    if (preset !== 'penthouse') this.bindSet(mat, 'preset_grass', TERRAIN_SIZE / 6, 0.8);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.receiveShadow = true;
    root.add(mesh);
  }

  /** A per-preset SUN with real shadows + hemisphere fill + scene fog. */
  private buildLighting(root: THREE.Group, preset: WorldPresetKey): void {
    const home = PRESET_HOME[preset as Exclude<WorldPresetKey, 'blank'>];
    const cfg = preset === 'mansion'
      ? { sun: 0xffd9a8, int: 2.6, dir: [0.7, 0.5, 0.15], hemi: [0xbfd4e8, 0x8a7a5e, 0.5], fog: [0xd8e2ea, 420, 1400] }
      : preset === 'penthouse'
        ? { sun: 0xa8b8e8, int: 1.1, dir: [-0.5, 0.6, 0.4], hemi: [0x33406a, 0x1c2030, 0.6], fog: [0x232b42, 300, 1200] }
        : { sun: 0xfff2d8, int: 2.2, dir: [0.4, 0.8, 0.3], hemi: [0xcfe0ea, 0x6a8a4e, 0.55], fog: [0xdde8ee, 380, 1300] };
    const sun = new THREE.DirectionalLight(cfg.sun, cfg.int);
    sun.position.set(home.x + cfg.dir[0] * 120, home.groundY + cfg.dir[1] * 160, home.z + cfg.dir[2] * 120);
    sun.target.position.set(home.x, home.groundY, home.z);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -90; sun.shadow.camera.right = 90;
    sun.shadow.camera.top = 90; sun.shadow.camera.bottom = -90;
    sun.shadow.camera.near = 10; sun.shadow.camera.far = 420;
    sun.shadow.bias = -0.0006;
    root.add(sun, sun.target);
    const hemi = new THREE.HemisphereLight(cfg.hemi[0] as number, cfg.hemi[1] as number, cfg.hemi[2] as number);
    root.add(hemi);
    this.hemi = hemi; seatHemisphereUp(hemi); // three reads the sky side from the light's WORLD position: under a shifted scene the default seat lit the home from the side
    this.scene.fog = new THREE.Fog(cfg.fog[0] as number, cfg.fog[1] as number, cfg.fog[2] as number);
  }

  // ── Shared architectural pieces ────────────────────────────────────────
  private box(parent: THREE.Object3D, w: number, h: number, d: number, x: number, y: number, z: number, mat: THREE.Material, yaw = 0): THREE.Mesh {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    m.position.set(x, y, z);
    m.rotation.y = yaw;
    m.castShadow = true;
    m.receiveShadow = true;
    parent.add(m);
    return m;
  }

  /** Mullioned glazing: a glass pane with a slim grid of glazing bars. */
  private glazing(parent: THREE.Object3D, w: number, h: number, x: number, y: number, z: number, trim: THREE.Material): void {
    const glass = new THREE.MeshStandardMaterial({ color: 0xaad0e8, roughness: 0.05, metalness: 0.35, transparent: true, opacity: 0.3 });
    const pane = new THREE.Mesh(new THREE.PlaneGeometry(w, h), glass);
    pane.position.set(x, y, z);
    pane.rotation.y = Math.PI;
    parent.add(pane);
    const cols = Math.max(2, Math.round(w / 1.1));
    for (let i = 1; i < cols; i++) {
      this.box(parent, 0.05, h, 0.05, x - w / 2 + (i * w) / cols, y, z, trim);
    }
    this.box(parent, w, 0.06, 0.06, x, y, z, trim);
    this.box(parent, w + 0.16, 0.14, 0.12, x, y - h / 2 - 0.05, z, trim); // sill
  }

  /** A designed home shell + its EnterableBuilding registration. */
  private buildHome(root: THREE.Group, spec: HomeSpec): THREE.Group {
    const g = new THREE.Group();
    g.position.set(spec.x, spec.groundY, spec.z);
    g.rotation.y = spec.yaw;
    // The lime plaster wears the EON rough diffuse with the tier's flag (lib/plaster_material — the only LIT plaster).
    const body = spec.wall !== 'none' ? buildPlasterMaterial(spec.body) : new THREE.MeshStandardMaterial({ color: spec.body, roughness: 0.62, metalness: 0.05 });
    if (spec.wall !== 'none') this.bindSet(body, 'preset_plaster', Math.max(2, spec.w / 4), 0.7);
    const trim = new THREE.MeshStandardMaterial({ color: spec.trim, roughness: 0.4, metalness: 0.3 });
    this.box(g, spec.w + 1.2, 0.4, spec.d + 1.2, 0, 0.2, 0, trim);
    this.box(g, spec.w, spec.h, spec.d, 0, spec.h / 2, 0, body);
    this.box(g, spec.w + 1.1, 0.4, spec.d + 1.1, 0, spec.h + 0.2, 0, trim); // eave/overhang
    // Front glazing flanking the door (local -Z), properly mullioned.
    const paneW = Math.max(1.2, (spec.w * 0.7 - 2.4) / 2);
    for (const sgn of [-1, 1]) {
      this.glazing(g, paneW, spec.h * 0.46, sgn * (1.2 + paneW / 2), 0.7 + spec.h * 0.23, -spec.d / 2 - 0.03, trim);
    }
    for (const sgn of [-1, 1]) this.box(g, 0.22, 3.0, 0.3, sgn * 1.15, 1.5, -spec.d / 2 - 0.1, trim);
    this.box(g, 2.6, 0.25, 0.3, 0, 3.05, -spec.d / 2 - 0.1, trim);
    root.add(g);

    const physics = this.physics;
    const cid = spec.id;
    const ent: EnterableBuilding = {
      id: spec.id,
      name: spec.name,
      key: 'preset:home',
      x: spec.x, z: spec.z, yaw: spec.yaw, groundY: spec.groundY,
      w: spec.w, h: spec.h, d: spec.d,
      hide: () => { g.visible = false; physics.removeStaticCollider(cid); },
      restore: () => {
        g.visible = true;
        physics.addStaticCollider(cid, { shape: 'box', halfExtents: [spec.w / 2, spec.h / 2, spec.d / 2] },
          { x: spec.x, y: spec.groundY + spec.h / 2, z: spec.z, yaw: spec.yaw, scale: 1 });
      },
    };
    enterables.set(spec.id, ent);
    this.enterableIds.push(spec.id);
    this.addCol(cid, [spec.w, spec.h, spec.d], spec.x, spec.groundY + spec.h / 2, spec.z, spec.yaw);
    // The ground-of-last-resort slab under every pad.
    this.addCol(`${spec.id}:safety`, [spec.w * 2, 1, spec.d * 2], spec.x, spec.groundY - 1.1, spec.z, 0);
    return g;
  }

  // ── MANSION: glass volumes over the sea, golden hour ───────────────────
  private buildMansion(root: THREE.Group): void {
    const hp = PRESET_HOME.mansion;
    const g = this.buildHome(root, {
      id: 'preset:home', name: 'the cliff mansion',
      ...hp, body: 0xe8e2d4, trim: 0x8a7a5e, wall: 'plaster',
    });
    const stoneMat = new THREE.MeshStandardMaterial({ color: 0xcdc4b0, roughness: 0.5, metalness: 0.1 });
    const trim = new THREE.MeshStandardMaterial({ color: 0x8a7a5e, roughness: 0.4, metalness: 0.3 });
    // Corner quoins + a floor-2 BALCONY over the sea side.
    for (const [qx, qz] of [[-hp.w / 2, -hp.d / 2], [hp.w / 2, -hp.d / 2], [-hp.w / 2, hp.d / 2], [hp.w / 2, hp.d / 2]] as Array<[number, number]>) {
      this.box(g, 0.6, hp.h, 0.6, qx, hp.h / 2, qz, stoneMat);
    }
    const balc = new THREE.Group();
    balc.position.set(0, 5.2, hp.d / 2 + 1.1);
    this.box(balc, 8, 0.3, 2.2, 0, 0, 0, stoneMat);
    for (let bx = -3.8; bx <= 3.8; bx += 0.8) this.box(balc, 0.12, 1.0, 0.12, bx, 0.65, 0.95, trim);
    this.box(balc, 8, 0.12, 0.12, 0, 1.2, 0.95, trim);
    g.add(balc);
    // The SEA — textured, rippling, to the horizon.
    this.seaMat = new THREE.MeshStandardMaterial({ color: 0x9ab8c8, roughness: 0.18, metalness: 0.55, transparent: true, opacity: 0.9 });
    this.bindSet(this.seaMat, 'preset_sea', 90, 1.4);
    const sea = new THREE.Mesh(new THREE.PlaneGeometry(1400, 1400).rotateX(-Math.PI / 2), this.seaMat);
    sea.position.set(MANSION_CLIFF_X1 + 640, MANSION_SEA_Y, 0);
    root.add(sea);
    // The BEACH: a sand-textured skin over the shore band (real sand map).
    {
      const bw = 44, bd = 440;
      const bgeo = new THREE.PlaneGeometry(bw, bd, 44, 110).rotateX(-Math.PI / 2);
      const bpos = bgeo.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < bpos.count; i++) {
        const wx = bpos.getX(i) + MANSION_CLIFF_X1 + 12;
        const wz = bpos.getZ(i);
        bpos.setY(i, presetGroundY('mansion', wx, wz) + 0.06);
      }
      bgeo.computeVertexNormals();
      const sand = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.95, metalness: 0.02 });
      this.bindSet(sand, 'preset_sand', 10, 0.9);
      const beach = new THREE.Mesh(bgeo, sand);
      beach.position.set(MANSION_CLIFF_X1 + 12, 0, 0);
      beach.receiveShadow = true;
      root.add(beach);
      // Surf foam where the sand meets the water.
      const foam = new THREE.Mesh(new THREE.PlaneGeometry(3.2, bd).rotateX(-Math.PI / 2),
        new THREE.MeshBasicMaterial({ color: 0xeafaff, transparent: true, opacity: 0.4, toneMapped: false }));
      foam.position.set(74.5, MANSION_SEA_Y + 0.04, 0);
      root.add(foam);
    }
    // A weathered JETTY off the ramp's foot, planked out over the shallows.
    {
      const plank = new THREE.MeshStandardMaterial({ color: 0x8a7458, roughness: 0.85 });
      const jz = (MANSION_RAMP_Z0 + MANSION_RAMP_Z1) / 2;
      for (let i = 0; i < 10; i++) {
        const jx = 66 + i * 2.1;
        this.box(root, 2.0, 0.12, 2.6, jx, 0.62, jz, plank);
        if (i % 2 === 0) {
          for (const sgn of [-1, 1]) this.box(root, 0.22, 2.6, 0.22, jx, -0.5, jz + sgn * 1.2, plank);
        }
      }
      this.addCol('preset:mansion:jetty', [21, 0.3, 2.6], 66 + 9.5, 0.62, jz, 0);
    }
    // The POOL ANNEX: a glass-walled volume cascading seaward — ENTERABLE.
    this.buildHome(root, {
      id: 'preset:annex', name: 'the pool annex',
      x: hp.x + 16, z: 9, yaw: Math.PI / 2, w: 12, d: 9, h: 4.6, groundY: 16,
      body: 0xdde4e2, trim: 0x8a7a5e, wall: 'plaster',
    });
    // The INFINITY POOL on the cliff edge (glowing rim at dusk).
    this.box(root, 11, 0.9, 6, 33, 16.45, -6, stoneMat);
    const pool = new THREE.Mesh(new THREE.PlaneGeometry(10.2, 5.2).rotateX(-Math.PI / 2),
      new THREE.MeshStandardMaterial({ color: 0x5fd8e8, roughness: 0.1, metalness: 0.4, transparent: true, opacity: 0.85, emissive: 0x1a5a6a, emissiveIntensity: 0.5 }));
    pool.position.set(33, 16.92, -6);
    root.add(pool);
    this.addCol('preset:mansion:pool', [11, 0.9, 6], 33, 16.45, -6, 0);
    const poolGlow = new THREE.PointLight(0x6fd8e8, 22, 26, 1.8);
    poolGlow.position.set(33, 17.6, -6);
    root.add(poolGlow);
    // Terrace balustrade along the cliff edge.
    for (let z = -18; z <= 18; z += 1.4) this.box(root, 0.12, 0.95, 0.12, 38.5, 16.5, z, trim);
    this.box(root, 0.16, 0.14, 38, 38.5, 17.05, 0, stoneMat);
    this.addCol('preset:mansion:rail', [0.6, 1.2, 40], 38.6, 16.6, 0, 0);
    // Fire lounge + cypress sentinels + stepped garden hedges.
    this.box(root, 2.2, 0.5, 2.2, 26, 16.25, -14, stoneMat);
    // THE FIRE LOUNGE BURNS (fire realism R12, 2026-09-25): a fire-system hearth on the slab — the flame on the tier's
    // rung, a glowing coal bed, embers, smoke and a light lent by the shared broker. It retired a flat orange cone and
    // the lounge's own 30 cd PointLight (one light less in every lit material of the mansion world).
    const lounge = fireAnchor({ kind: 'hearth', x: 26, y: 16.58, z: -14, groundDrop: 0.08, bed: true });
    if (lounge) root.add(lounge);
    const hedge = new THREE.MeshStandardMaterial({ color: 0x35522e, roughness: 0.95 });
    for (const [hx, hz, hl] of [[-2, -12, 14], [-2, 12, 14], [-12, 0, 18]] as Array<[number, number, number]>) {
      this.box(root, hx === -12 ? 1.2 : hl, 1.1, hx === -12 ? hl : 1.2, hx, 16.55, hz, hedge);
    }
    const cyp = new THREE.MeshStandardMaterial({ color: 0x2e4a2a, roughness: 0.9 });
    for (const [tx, tz] of [[-6, -16], [-6, 16], [2, -20], [2, 20], [-16, 0]] as Array<[number, number]>) {
      const t = new THREE.Mesh(new THREE.ConeGeometry(1.1, 6.5, 8), cyp);
      t.position.set(tx, presetGroundY('mansion', tx, tz) + 3.2, tz);
      t.castShadow = true;
      root.add(t);
    }
  }

  // ── PENTHOUSE: dusk on the sky deck over a glowing city ────────────────
  private buildPenthouse(root: THREE.Group): void {
    const hp = PRESET_HOME.penthouse;
    this.buildHome(root, {
      id: 'preset:home', name: 'the penthouse',
      ...hp, body: 0x3c424e, trim: 0xc8a24e, wall: 'none',
    });
    const deckMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.15 });
    this.bindSet(deckMat, 'preset_deck', 10, 0.8);
    const glass = new THREE.MeshStandardMaterial({ color: 0xaad4f0, roughness: 0.05, metalness: 0.3, transparent: true, opacity: 0.28 });
    // Tower shaft + textured deck slab.
    this.box(root, PENT_DECK_HW * 2, PENT_DECK_Y, PENT_DECK_HD * 2, 0, PENT_DECK_Y / 2, 0,
      new THREE.MeshStandardMaterial({ color: 0x2c313a, roughness: 0.6, metalness: 0.3 }));
    const deckSlab = new THREE.Mesh(new THREE.BoxGeometry(PENT_DECK_HW * 2 + 1, 0.3, PENT_DECK_HD * 2 + 1), deckMat);
    deckSlab.position.set(0, PENT_DECK_Y + 0.15, 0);
    deckSlab.receiveShadow = true;
    root.add(deckSlab);
    // Glass parapet + colliders.
    for (const [w, d, x, z] of [
      [PENT_DECK_HW * 2 + 1, 0.15, 0, -PENT_DECK_HD - 0.4], [PENT_DECK_HW * 2 + 1, 0.15, 0, PENT_DECK_HD + 0.4],
      [0.15, PENT_DECK_HD * 2 + 1, -PENT_DECK_HW - 0.4, 0], [0.15, PENT_DECK_HD * 2 + 1, PENT_DECK_HW + 0.4, 0],
    ] as Array<[number, number, number, number]>) {
      const p = new THREE.Mesh(new THREE.BoxGeometry(w, 1.3, d), glass);
      p.position.set(x, PENT_DECK_Y + 0.95, z);
      root.add(p);
      this.addCol(`preset:pent:rail:${x}:${z}`, [Math.max(w, 0.6), 1.5, Math.max(d, 0.6)], x, PENT_DECK_Y + 1.0, z, 0);
    }
    // The SKY LOUNGE — a second enterable glass volume across the terrace.
    this.buildHome(root, {
      id: 'preset:lounge', name: 'the sky lounge',
      x: -12, z: 10, yaw: Math.PI, w: 10, d: 8, h: 4.2, groundY: PENT_DECK_Y,
      body: 0x46505c, trim: 0xc8a24e, wall: 'none',
    });
    // Terrace: lounges, planters, spa, STRING LIGHTS sagging between poles.
    const lounge = new THREE.MeshStandardMaterial({ color: 0x8a6a4e, roughness: 0.7 });
    const leaf = new THREE.MeshStandardMaterial({ color: 0x3e6a38, roughness: 0.9 });
    for (const [x, z] of [[-12, -12], [-6, -13], [12, -12]] as Array<[number, number]>) {
      this.box(root, 2.4, 0.5, 1.1, x, PENT_DECK_Y + 0.55, z, lounge);
    }
    for (const [x, z] of [[-17, -13], [17, -13], [17, 13]] as Array<[number, number]>) {
      this.box(root, 1.2, 0.7, 1.2, x, PENT_DECK_Y + 0.65, z, deckMat);
      const bush = new THREE.Mesh(new THREE.IcosahedronGeometry(0.8, 1), leaf);
      bush.position.set(x, PENT_DECK_Y + 1.6, z);
      root.add(bush);
    }
    const spa = new THREE.Mesh(new THREE.PlaneGeometry(6, 2.6).rotateX(-Math.PI / 2),
      new THREE.MeshStandardMaterial({ color: 0x5fd8e8, roughness: 0.1, metalness: 0.4, transparent: true, opacity: 0.85, emissive: 0x1a5a6a, emissiveIntensity: 0.6 }));
    spa.position.set(12, PENT_DECK_Y + 0.34, 10);
    root.add(spa);
    const bulbMat = new THREE.MeshBasicMaterial({ color: 0xffd98a, toneMapped: false });
    const bulbGeo = new THREE.SphereGeometry(0.09, 6, 6);
    const poleMat = new THREE.MeshStandardMaterial({ color: 0x2a2e36, roughness: 0.5, metalness: 0.6 });
    const poles: Array<[number, number]> = [[-18, -14], [0, -15], [18, -14], [18, 8], [0, 14]];
    for (const [px, pz] of poles) this.box(root, 0.1, 3.2, 0.1, px, PENT_DECK_Y + 1.6, pz, poleMat);
    for (let i = 0; i < poles.length - 1; i++) {
      const [ax, az] = poles[i], [bx, bz] = poles[i + 1];
      for (let t = 0.1; t < 1; t += 0.12) {
        const bulb = new THREE.Mesh(bulbGeo, bulbMat);
        const sag = Math.sin(t * Math.PI) * 0.7;
        bulb.position.set(ax + (bx - ax) * t, PENT_DECK_Y + 3.2 - sag, az + (bz - az) * t);
        root.add(bulb);
      }
    }
    const warm = new THREE.PointLight(0xffd9a0, 55, 60, 1.8);
    warm.position.set(0, PENT_DECK_Y + 6, -8);
    root.add(warm);
    // The city below — two rings of emissive-window towers + a beacon.
    const cv = document.createElement('canvas');
    cv.width = 64; cv.height = 128;
    const g2 = cv.getContext('2d')!;
    g2.fillStyle = '#12151c'; g2.fillRect(0, 0, 64, 128);
    for (let wy = 4; wy < 124; wy += 8) for (let wx = 4; wx < 60; wx += 8) {
      g2.fillStyle = Math.random() < 0.55 ? '#ffd98a' : '#1c2027';
      g2.fillRect(wx, wy, 5, 5);
    }
    const winTex = new THREE.CanvasTexture(cv);
    winTex.colorSpace = THREE.SRGBColorSpace;
    const towerMat = new THREE.MeshStandardMaterial({ map: winTex, emissiveMap: winTex, emissive: 0xffffff, emissiveIntensity: 0.7, color: 0x222630, roughness: 0.7 });
    const towers = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), towerMat, 72);
    const m4 = new THREE.Matrix4();
    let h = 0x9e3779b9;
    const rnd = (): number => { h ^= h << 13; h ^= h >>> 17; h ^= h << 5; return ((h >>> 0) % 1000) / 1000; };
    for (let i = 0; i < 72; i++) {
      const ang = (i / 72) * Math.PI * 2 + rnd() * 0.08;
      const r = i < 44 ? 90 + rnd() * 220 : 340 + rnd() * 240;
      const th = 24 + rnd() * (i < 44 ? 58 : 80);
      const tw = 14 + rnd() * 16;
      m4.makeTranslation(Math.sin(ang) * r, th / 2, Math.cos(ang) * r);
      m4.scale(new THREE.Vector3(tw, th, tw));
      towers.setMatrixAt(i, m4);
    }
    towers.instanceMatrix.needsUpdate = true;
    root.add(towers);
    const antenna = this.box(root, 0.3, 8, 0.3, PENT_DECK_HW - 2, PENT_DECK_Y + 4, PENT_DECK_HD - 2, poleMat);
    void antenna;
    const beacon = new THREE.Mesh(new THREE.SphereGeometry(0.3, 8, 8), new THREE.MeshBasicMaterial({ color: 0xff4040, toneMapped: false }));
    beacon.position.set(PENT_DECK_HW - 2, PENT_DECK_Y + 8.2, PENT_DECK_HD - 2);
    root.add(beacon);
  }

  // ── COTTAGE: whitewash + thatch in a wildflower meadow, soft noon ──────
  private buildCottage(root: THREE.Group): void {
    const hp = PRESET_HOME.cottage;
    const g = this.buildHome(root, {
      id: 'preset:home', name: 'the cottage',
      ...hp, body: 0xf0e8da, trim: 0x6a4a34, wall: 'plaster',
    });
    // The THATCH (textured) + chimney + shutters + door lantern + roses.
    const thatch = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.95 });
    this.bindSet(thatch, 'preset_thatch', 3, 1.1);
    const roof = new THREE.Mesh(new THREE.CylinderGeometry(0.01, hp.w * 0.78, 2.6, 4, 1), thatch);
    roof.rotation.y = Math.PI / 4;
    roof.position.set(hp.x, hp.groundY + hp.h + 1.4, hp.z);
    roof.scale.z = hp.d / hp.w;
    roof.castShadow = true;
    root.add(roof);
    const brick = new THREE.MeshStandardMaterial({ color: 0x8a6a52, roughness: 0.8 });
    this.box(root, 0.9, 2.4, 0.9, hp.x + hp.w / 2 - 1.4, hp.groundY + hp.h + 1.2, hp.z + 1, brick);
    const shutter = new THREE.MeshStandardMaterial({ color: 0x4a6a4e, roughness: 0.7 });
    const paneW = Math.max(1.2, (hp.w * 0.7 - 2.4) / 2);
    for (const sgn of [-1, 1]) {
      for (const ss of [-1, 1]) {
        this.box(g, 0.45, hp.h * 0.4, 0.08, sgn * (1.2 + paneW / 2) + ss * (paneW / 2 + 0.3), 0.7 + hp.h * 0.23, -hp.d / 2 - 0.06, shutter);
      }
    }
    const lantern = new THREE.Mesh(new THREE.SphereGeometry(0.16, 8, 8), new THREE.MeshBasicMaterial({ color: 0xffd98a, toneMapped: false }));
    lantern.position.set(hp.x + 1.6, hp.groundY + 2.6, hp.z - hp.d / 2 - 0.25);
    root.add(lantern);
    const lanternGlow = new THREE.PointLight(0xffd9a0, 14, 16, 1.8);
    lanternGlow.position.copy(lantern.position);
    root.add(lanternGlow);
    const rose = new THREE.MeshStandardMaterial({ color: 0x3e6a38, roughness: 0.95 });
    this.box(root, 0.5, hp.h * 0.8, 0.25, hp.x - hp.w / 2 + 0.8, hp.groundY + hp.h * 0.4, hp.z - hp.d / 2 - 0.15, rose);
    // The GARDEN WORKSHOP — a second enterable outbuilding.
    this.buildHome(root, {
      id: 'preset:shed', name: 'the garden workshop',
      x: hp.x + 12, z: hp.z + 7, yaw: Math.PI / 2, w: 8, d: 7, h: 4.4, groundY: presetGroundY('cottage', hp.x + 12, hp.z + 7),
      body: 0xd8cfba, trim: 0x5e4630, wall: 'plaster',
    });
    // Picket fence + gate, gravel path, flower BEDS along the fence.
    const picket = new THREE.MeshStandardMaterial({ color: 0xe8e4dc, roughness: 0.8 });
    const R = 19;
    for (let i = 0; i < 48; i++) {
      const a = (i / 48) * Math.PI * 2;
      if (Math.abs(a - Math.PI * 1.5) < 0.15) continue;
      const px = hp.x + Math.cos(a) * R, pz = hp.z + Math.sin(a) * R;
      this.box(root, 0.14, 1.0, 0.5, px, presetGroundY('cottage', px, pz) + 0.5, pz, picket, -a);
    }
    this.addCol('preset:cottage:fence:w', [0.4, 1.2, R * 1.6], hp.x - R, 1.2, hp.z, 0);
    this.addCol('preset:cottage:fence:e', [0.4, 1.2, R * 1.6], hp.x + R, 1.2, hp.z, 0);
    this.addCol('preset:cottage:fence:n', [R * 1.6, 1.2, 0.4], hp.x, 1.2, hp.z + R, 0);
    // REAL PATHS: continuous dirt ribbons hugging the meadow — door → gate,
    // door → workshop, door → well (the storybook approach lines).
    const pathMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.95, metalness: 0.02 });
    this.bindSet(pathMat, 'preset_path', 1, 0.9);
    const wob = (t: number, amp: number): number => Math.sin(t * 9.7) * amp;
    this.pathRibbon(root, Array.from({ length: 22 }, (_, i) => {
      const t = i / 21;
      return { x: hp.x + wob(t, 0.9), z: hp.z - hp.d / 2 - 1.2 - t * 24 };
    }), 1.8, pathMat);
    this.pathRibbon(root, Array.from({ length: 14 }, (_, i) => {
      const t = i / 13;
      return { x: hp.x + 2 + t * 9.2 + wob(t, 0.5), z: hp.z + 1 + t * 5.2 };
    }), 1.4, pathMat);
    this.pathRibbon(root, Array.from({ length: 12 }, (_, i) => {
      const t = i / 11;
      return { x: hp.x - 1 - t * 6.4 + wob(t, 0.4), z: hp.z + 1.5 + t * 5.8 };
    }), 1.3, pathMat);
    // REAL trees + undergrowth + WILDFLOWERS: the cooked foliage GLBs, with a
    // DISTANT WOODS ring closing the horizon (instanced — one draw per part).
    // The flower beds/drifts live in buildCottageFlora too (real GLB species,
    // not colored cones).
    void this.buildCottageFlora(root);
    for (const [tx, tz] of [[-26, 18], [24, -20], [10, 34], [-30, -24]] as Array<[number, number]>) {
      const gy = presetGroundY('cottage', tx, tz);
      this.addCol(`preset:cottage:tree:${tx}`, [0.9, 3.6, 0.9], tx, gy + 1.8, tz, 0);
    }
    const stone = new THREE.MeshStandardMaterial({ color: 0x8a8278, roughness: 0.8 });
    const well = new THREE.Mesh(new THREE.CylinderGeometry(1.1, 1.2, 1.0, 12), stone);
    const wgy = presetGroundY('cottage', hp.x - 8, hp.z + 8);
    well.position.set(hp.x - 8, wgy + 0.5, hp.z + 8);
    root.add(well);
    const wellRoof = new THREE.Mesh(new THREE.ConeGeometry(1.5, 0.9, 8), thatch);
    wellRoof.position.set(hp.x - 8, wgy + 2.2, hp.z + 8);
    root.add(wellRoof);
    this.addCol('preset:cottage:well', [2.4, 1.2, 2.4], hp.x - 8, wgy + 0.6, hp.z + 8, 0);
  }

  /** A continuous ground-hugging path ribbon along a polyline. */
  private pathRibbon(root: THREE.Group, pts: Array<{ x: number; z: number }>, width: number, mat: THREE.Material): void {
    const n = pts.length;
    const positions = new Float32Array(n * 2 * 3);
    const uvs = new Float32Array(n * 2 * 2);
    const indices: number[] = [];
    let dist = 0;
    for (let i = 0; i < n; i++) {
      const p = pts[i];
      const q = pts[Math.min(i + 1, n - 1)];
      const p0 = pts[Math.max(i - 1, 0)];
      let dx = q.x - p0.x, dz = q.z - p0.z;
      const len = Math.hypot(dx, dz) || 1;
      dx /= len; dz /= len;
      const nx = -dz, nz = dx; // left normal
      if (i > 0) dist += Math.hypot(p.x - pts[i - 1].x, p.z - pts[i - 1].z);
      for (const [side, sgn] of [[0, -1], [1, 1]] as Array<[number, number]>) {
        const vx = p.x + nx * sgn * width / 2;
        const vz = p.z + nz * sgn * width / 2;
        const vi = (i * 2 + side) * 3;
        positions[vi] = vx;
        positions[vi + 1] = presetGroundY('cottage', vx, vz) + 0.05;
        positions[vi + 2] = vz;
        const ui = (i * 2 + side) * 2;
        uvs[ui] = side;
        uvs[ui + 1] = dist / width;
      }
      if (i < n - 1) {
        const a2 = i * 2;
        indices.push(a2, a2 + 1, a2 + 2, a2 + 1, a2 + 3, a2 + 2);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    geo.setIndex(indices);
    geo.computeVertexNormals();
    const mesh = new THREE.Mesh(geo, mat);
    mesh.receiveShadow = true;
    root.add(mesh);
  }

  /** The cottage's living flora: near oaks (real canopy GLBs), fence-line
   *  undergrowth, and a WOODS ring of ~140 instanced trees on the horizon. */
  private async buildCottageFlora(root: THREE.Group): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    const load = async (prefab: string): Promise<THREE.Object3D | null> => {
      const id = prefabAssetId(conn, prefab);
      if (id === null) return null;
      try {
        const master = await this.assets.load(id);
        markShared(master);
        return master;
      } catch { return null; }
    };
    const [tree, bush, fern, berry, ...flowers] = await Promise.all([
      load('foliage:canopy_tree_broad'), load('foliage:bush'), load('foliage:fern_bush'), load('foliage:berry_bush'),
      // Five REAL flower species for the beds + meadow drifts (no cones).
      load('foliage:dustbell_flower'), load('foliage:trumpet_bloom'), load('foliage:star_bloom'),
      load('foliage:scree_heather'), load('foliage:mist_orchid'),
    ]);
    if (this.root !== root) return; // left the world while loading
    const fitH = (obj: THREE.Object3D, target: number): number => {
      const bb = new THREE.Box3().setFromObject(obj);
      return target / Math.max(0.01, bb.max.y - bb.min.y);
    };
    let h2 = 0x51ed270b;
    const rnd = (): number => { h2 ^= h2 << 13; h2 ^= h2 >>> 17; h2 ^= h2 << 5; return ((h2 >>> 0) % 1000) / 1000; };
    // Near oaks: full clones (shadowed) at the old positions.
    if (tree) {
      const base = fitH(tree, 8.4);
      for (const [tx, tz] of [[-26, 18], [24, -20], [10, 34], [-30, -24]] as Array<[number, number]>) {
        const clone = tree.clone();
        clone.scale.setScalar(base * (0.9 + rnd() * 0.35));
        clone.rotation.y = rnd() * Math.PI * 2;
        clone.position.set(tx, presetGroundY('cottage', tx, tz), tz);
        clone.traverse((o) => { o.castShadow = true; o.userData.sharedAsset = true; });
        root.add(clone);
      }
      // The WOODS: an instanced ring r 150–330 closing the horizon.
      const parts = collectMeshParts(tree);
      const N = 140;
      const mats: THREE.Matrix4[] = [];
      const m4 = new THREE.Matrix4();
      const q = new THREE.Quaternion();
      const up = new THREE.Vector3(0, 1, 0);
      const sc = new THREE.Vector3();
      for (let i = 0; i < N; i++) {
        const ang = rnd() * Math.PI * 2;
        const r = 150 + rnd() * 180;
        const px = Math.cos(ang) * r, pz = Math.sin(ang) * r;
        const scale = base * (0.85 + rnd() * 0.6);
        q.setFromAxisAngle(up, rnd() * Math.PI * 2);
        m4.compose(new THREE.Vector3(px, presetGroundY('cottage', px, pz), pz), q, sc.set(scale, scale, scale));
        mats.push(m4.clone());
      }
      for (const part of parts) {
        const inst = new THREE.InstancedMesh(part.geometry, part.material, N);
        (inst as unknown as { userData: Record<string, unknown> }).userData.sharedAsset = true;
        for (let i = 0; i < N; i++) {
          m4.copy(mats[i]).multiply(part.local);
          inst.setMatrixAt(i, m4);
        }
        inst.instanceMatrix.needsUpdate = true;
        root.add(inst);
      }
    }
    // Undergrowth: bushes + ferns + berry bushes drifting through the meadow.
    for (const [master, count, target] of [[bush, 40, 1.3], [fern, 30, 1.0], [berry, 20, 1.1]] as Array<[THREE.Object3D | null, number, number]>) {
      if (!master) continue;
      const base = fitH(master, target);
      const parts = collectMeshParts(master);
      const mats: THREE.Matrix4[] = [];
      const m4 = new THREE.Matrix4();
      const q = new THREE.Quaternion();
      const up = new THREE.Vector3(0, 1, 0);
      const sc = new THREE.Vector3();
      for (let i = 0; i < count; i++) {
        const ang = rnd() * Math.PI * 2;
        const r = 12 + rnd() * 130;
        const px = Math.cos(ang) * r, pz = Math.sin(ang) * r;
        const scale = base * (0.8 + rnd() * 0.5);
        q.setFromAxisAngle(up, rnd() * Math.PI * 2);
        m4.compose(new THREE.Vector3(px, presetGroundY('cottage', px, pz), pz), q, sc.set(scale, scale, scale));
        mats.push(m4.clone());
      }
      for (const part of parts) {
        const inst = new THREE.InstancedMesh(part.geometry, part.material, count);
        (inst as unknown as { userData: Record<string, unknown> }).userData.sharedAsset = true;
        for (let i = 0; i < count; i++) {
          m4.copy(mats[i]).multiply(part.local);
          inst.setMatrixAt(i, m4);
        }
        inst.instanceMatrix.needsUpdate = true;
        root.add(inst);
      }
    }
    // WILDFLOWERS: five real species replace the old colored-cone tufts —
    // the showy blooms fill the fence-line BEDS (first `beds` placements hug
    // the picket circle), the rest drift the open meadow.
    const hp = PRESET_HOME.cottage;
    const fenceR = 19; // the picket-fence radius in buildCottage
    for (const [master, count, beds, target] of [
      [flowers[0], 130, 0, 0.55],  // dustbell — meadow drifts
      [flowers[1], 110, 40, 0.6],  // trumpet bloom — beds + drifts
      [flowers[2], 110, 40, 0.5],  // star bloom — beds + drifts
      [flowers[3], 110, 0, 0.5],   // heather — meadow drifts
      [flowers[4], 100, 40, 0.6],  // orchid — beds + drifts
    ] as Array<[THREE.Object3D | null, number, number, number]>) {
      if (!master) continue;
      const base = fitH(master, target);
      const parts = collectMeshParts(master);
      const mats: THREE.Matrix4[] = [];
      const m4 = new THREE.Matrix4();
      const q = new THREE.Quaternion();
      const up = new THREE.Vector3(0, 1, 0);
      const sc = new THREE.Vector3();
      for (let i = 0; i < count; i++) {
        const ang = rnd() * Math.PI * 2;
        const r = i < beds ? fenceR - 1.2 - rnd() * 1.4 : 8 + rnd() * 160;
        const px = hp.x + Math.cos(ang) * r, pz = hp.z + Math.sin(ang) * r;
        const scale = base * (0.75 + rnd() * 0.5);
        q.setFromAxisAngle(up, rnd() * Math.PI * 2);
        m4.compose(new THREE.Vector3(px, presetGroundY('cottage', px, pz), pz), q, sc.set(scale, scale, scale));
        mats.push(m4.clone());
      }
      for (const part of parts) {
        const inst = new THREE.InstancedMesh(part.geometry, part.material, count);
        (inst as unknown as { userData: Record<string, unknown> }).userData.sharedAsset = true;
        for (let i = 0; i < count; i++) {
          m4.copy(mats[i]).multiply(part.local);
          inst.setMatrixAt(i, m4);
        }
        inst.instanceMatrix.needsUpdate = true;
        root.add(inst);
      }
    }
  }

  // ── Teardown ───────────────────────────────────────────────────────────
  private release(): void {
    if (!this.root) return;
    this.scene.remove(this.root);
    this.scene.fog = this.hadFog ? this.savedFog : null;
    this.seaMat = null;
    this.hemi = null;
    const seen = new Set<object>();
    this.root.traverse((o) => {
      const mesh = o as THREE.Mesh & { isInstancedMesh?: boolean; dispose?(): void };
      if (o.userData.sharedAsset) {
        // Asset-cache geometry/materials (flora GLBs) — release the instance
        // buffers only, never the shared payload (the shared-asset law).
        if (mesh.isInstancedMesh && typeof mesh.dispose === 'function') disposeLater(mesh as unknown as { dispose(): void });
        return;
      }
      if (mesh.geometry && !seen.has(mesh.geometry)) { seen.add(mesh.geometry); disposeLater(mesh.geometry); }
      const mats = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
      for (const mat of mats) if (!seen.has(mat)) { seen.add(mat); disposeLater(mat); } // textures stay in the AssetModule cache
      if (mesh.isInstancedMesh && typeof mesh.dispose === 'function') disposeLater(mesh as unknown as { dispose(): void });
    });
    this.root = null;
    this.builtKey = '';
    this.activePreset = null;
    if (this.swimming) { this.swimming = false; this.physics.gravityScale = 1; setUnderwaterFog(null); atlas.submerged = false; }
    for (const id of this.colliderIds) this.physics.removeStaticCollider(id);
    this.colliderIds = [];
    for (const id of this.enterableIds) enterables.delete(id);
    this.enterableIds = [];
  }

  dispose(): void {
    // (bug sweep) the 2.5 s world-scan poll, the 15 Hz swim tick and the Space handler outlived the module.
    if (this.refreshTimer !== null) { window.clearInterval(this.refreshTimer); this.refreshTimer = null; }
    if (this.swimTimer !== null) { window.clearInterval(this.swimTimer); this.swimTimer = null; }
    if (this.onSwimKey) { window.removeEventListener('keydown', this.onSwimKey); this.onSwimKey = null; }
    this.release();
  }
}
